<template>
  <div />
</template>

<script>

/**
 * Vue: Depict Search (depict-search)
 * -----------------------------------------------------------------------------
 * Component to make Depict Search API calls.
 * https://api.depict.ai/docs#tag/Search-(v3)
 *
 * @param {String} merchant - Depict merchant required for the API.
 * @param {Number} limit - Number of products to retrieve per page.
 * @param {Number} offset - Number of products already loaded on page.
 * @param {String} cursor - Cursor for pagination
 * @param {String} query - Search query.
 * @param {Array} selectedFilters - Currently selected filters.
 *
 */

export default {
  name: 'DepictSearch',

  props: {
    merchant: {
      type: String,
      default: '',
    },
    limit: {
      type: Number,
      default: null,
    },
    offset: {
      type: Number,
      default: null,
    },
    cursor: {
      type: String,
      default: null,
    },
    query: {
      type: String,
      default: '',
    },
    selectedFilters: {
      type: Array,
      default: () => [],
    },
  },

  emits: {
    'search-updated': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      cnvsGlobal: true,
    }
  },

  watch: {

    /**
     * Watch selected filters update to get product results.
     */
    selectedFilters() {
      this.getProductResults()
    },

    /**
     * Watch query update to get product results.
     */
    query() {
      this.getProductResults()
    },

    /**
     * Watch offset update to get product results.
     */
    offset() {
      if (!this.offset) {
        return
      }

      this.getProductResults('offset')
    },
  },

  methods: {

    /**
     * Handle API call to Depict.
     * @param {String} action - Action carried out to call function.
     */
    async getProductResults(action = 'default') {
      if (!this.query.length) {
        return
      }

      try {
        // Generate or retrieve session_id
        let sessionId = sessionStorage.getItem('session_id')
        if (!sessionId) {
          sessionId = self.crypto.randomUUID()
          sessionStorage.setItem('session_id', sessionId)
        }

        const url = `https://api.depict.ai/v3/search/results`

        const market = cnvs.store.country.toLowerCase()
        const locale = cnvs.store.language

        // Added top variables to stop line size stopping commits
        const petiteMarkets = window.cnvs.depict.petiteMarkets
        const marketHandle = window.cnvs.store.marketHandle
        const isPetiteRegion = petiteMarkets.includes(marketHandle)
        const petiteFilter = window.cnvs.depict.showPetite && isPetiteRegion ? ['true', 'false'] : ['false']

        const filters = [
          ...this.selectedFilters.map(filter => ({
            field: filter.key,
            data: filter.values,
            op: filter.op,
          })),
          {
            field: 'app_exclusive',
            data: false,
            op: 'eq',
          },
          {
            field: 'is_petite',
            op: 'in',
            data: petiteFilter,
          },
        ]

        const queryObject = {
          merchant: this.merchant,
          market,
          locale,
          limit: this.limit,
          cursor: this.cursor,
          query: this.query,
          filters,
          session_id: sessionId,
        }

        const [productResults, suggestionResults] = await Promise.all([
          fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(queryObject),
          }).then(response => response.json()),
          fetch(
            `https://api.depict.ai/v3/search/suggestions?merchant=${this.merchant}&market=${market}&locale=${locale}&query=${this.query}`,
          ).then(response => response.json()),
        ])

        const data = {
          productResults,
          suggestionResults,
        }

        data.productResults.offset = this.offset

        this.$emit('search-updated', { data, action })
      } catch (error) {
        throw new Error(error)
      }
    },
  },

}
</script>
